import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RequestsDashboard = ({ filters, onShowForm }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [bulkSelection, setBulkSelection] = useState([]);

  // Mock requests data - in real app, this would come from API
  const mockRequests = [
    {
      id: 1,
      employeeName: 'Sarah Johnson',
      employeeAvatar: '/api/placeholder/32/32',
      department: 'Engineering',
      leaveType: 'Vacation',
      startDate: '2024-11-25',
      endDate: '2024-11-29',
      days: 5,
      status: 'pending',
      submittedAt: '2024-11-10T10:30:00Z',
      reason: 'Family vacation to Europe',
      managerId: 'mgr_001',
      managerName: 'Mike Chen',
      priority: 'normal'
    },
    {
      id: 2,
      employeeName: 'David Wilson',
      employeeAvatar: '/api/placeholder/32/32',
      department: 'Marketing',
      leaveType: 'Sick Leave',
      startDate: '2024-11-12',
      endDate: '2024-11-12',
      days: 1,
      status: 'approved',
      submittedAt: '2024-11-12T08:15:00Z',
      reason: 'Doctor appointment',
      managerId: 'mgr_002',
      managerName: 'Lisa Zhang',
      priority: 'urgent'
    },
    {
      id: 3,
      employeeName: 'Emma Thompson',
      employeeAvatar: '/api/placeholder/32/32',
      department: 'Design',
      leaveType: 'Personal',
      startDate: '2024-11-20',
      endDate: '2024-11-22',
      days: 3,
      status: 'denied',
      submittedAt: '2024-11-05T14:20:00Z',
      reason: 'Personal matters',
      managerId: 'mgr_003',
      managerName: 'John Smith',
      priority: 'normal',
      denialReason: 'Critical project deadline during requested dates'
    }
  ];

  const statusColors = {
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'Clock' },
    approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'CheckCircle' },
    denied: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'XCircle' }
  };

  const leaveTypeColors = {
    'Vacation': 'bg-blue-100 text-blue-800',
    'Sick Leave': 'bg-red-100 text-red-800',
    'Personal': 'bg-green-100 text-green-800',
    'Maternity/Paternity': 'bg-purple-100 text-purple-800',
    'Bereavement': 'bg-gray-100 text-gray-800'
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return formatDate(dateString);
  };

  const handleBulkSelection = (requestId) => {
    setBulkSelection(prev => 
      prev?.includes(requestId) 
        ? prev?.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk ${action} for requests:`, bulkSelection);
    setBulkSelection([]);
  };

  return (
    <div className="p-6">
      {/* Dashboard Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Time Off Requests</h3>
          <p className="text-sm text-gray-600 mt-1">
            {mockRequests?.length} total requests • {mockRequests?.filter(r => r?.status === 'pending')?.length} pending approval
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {bulkSelection?.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{bulkSelection?.length} selected</span>
              <button
                onClick={() => handleBulkAction('approve')}
                className="px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                Bulk Approve
              </button>
              <button
                onClick={() => handleBulkAction('deny')}
                className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                Bulk Deny
              </button>
            </div>
          )}
          
          <button
            onClick={onShowForm}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all"
          >
            <Icon name="Plus" size={16} />
            New Request
          </button>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Pending Approval', value: mockRequests?.filter(r => r?.status === 'pending')?.length, color: 'amber' },
          { label: 'Approved Today', value: 3, color: 'emerald' },
          { label: 'Average Response Time', value: '2.5 hours', color: 'blue' }
        ]?.map((stat, index) => (
          <div key={index} className="bg-gray-50/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat?.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat?.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-lg bg-${stat?.color}-100 flex items-center justify-center`}>
                <Icon name="Clock" size={20} className={`text-${stat?.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Requests List */}
      <div className="space-y-4">
        {mockRequests?.map((request) => {
          const status = statusColors?.[request?.status];
          const isSelected = bulkSelection?.includes(request?.id);
          
          return (
            <div
              key={request?.id}
              className={`bg-white border rounded-xl p-6 hover:shadow-md transition-all cursor-pointer ${
                isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : 'border-gray-200'
              }`}
              onClick={() => setSelectedRequest(request)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {/* Selection Checkbox */}
                  <div
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleBulkSelection(request?.id);
                    }}
                    className="mt-1"
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                      isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 hover:border-indigo-400'
                    }`}>
                      {isSelected && <Icon name="Check" size={12} className="text-white" />}
                    </div>
                  </div>
                  
                  {/* Employee Info */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {request?.employeeName?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{request?.employeeName}</h4>
                      <p className="text-sm text-gray-600">{request?.department}</p>
                    </div>
                  </div>
                  
                  {/* Request Details */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 ml-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Leave Type</p>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mt-1 ${leaveTypeColors?.[request?.leaveType] || 'bg-gray-100 text-gray-800'}`}>
                        {request?.leaveType}
                      </span>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-900">Duration</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {formatDate(request?.startDate)} - {formatDate(request?.endDate)} ({request?.days} day{request?.days > 1 ? 's' : ''})
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-900">Submitted</p>
                      <p className="text-sm text-gray-600 mt-1">{formatRelativeTime(request?.submittedAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Status and Actions */}
                <div className="flex items-center gap-3">
                  {request?.priority === 'urgent' && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded-lg">
                      <Icon name="AlertTriangle" size={14} className="text-red-600" />
                      <span className="text-xs font-medium text-red-700">Urgent</span>
                    </div>
                  )}
                  
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${status?.bg} ${status?.text} ${status?.border}`}>
                    <Icon name={status?.icon} size={14} />
                    <span className="text-sm font-medium capitalize">{request?.status}</span>
                  </div>
                  
                  {request?.status === 'pending' && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          console.log('Approve request:', request?.id);
                        }}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Approve"
                      >
                        <Icon name="Check" size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          console.log('Deny request:', request?.id);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Deny"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Reason */}
              {request?.reason && (
                <div className="mt-4 pl-16">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Reason:</span> {request?.reason}
                  </p>
                </div>
              )}
              {/* Denial Reason */}
              {request?.status === 'denied' && request?.denialReason && (
                <div className="mt-4 pl-16 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    <span className="font-medium">Denial Reason:</span> {request?.denialReason}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Empty State */}
      {mockRequests?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileText" size={48} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No time off requests</h3>
          <p className="text-gray-600 mb-6">Get started by submitting your first time off request.</p>
          <button
            onClick={onShowForm}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            <Icon name="Plus" size={18} />
            Submit Your First Request
          </button>
        </div>
      )}
      {/* Request Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Request Details</h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Employee & Request Info */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {selectedRequest?.employeeName?.split(' ')?.map(n => n?.[0])?.join('')}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900">{selectedRequest?.employeeName}</h4>
                  <p className="text-gray-600">{selectedRequest?.department}</p>
                  <div className="mt-2 flex items-center gap-4">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${leaveTypeColors?.[selectedRequest?.leaveType]}`}>
                      {selectedRequest?.leaveType}
                    </span>
                    <span className="text-sm text-gray-600">
                      {selectedRequest?.days} day{selectedRequest?.days > 1 ? 's' : ''} requested
                    </span>
                  </div>
                </div>
              </div>

              {/* Request Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <p className="text-gray-900">{formatDate(selectedRequest?.startDate)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <p className="text-gray-900">{formatDate(selectedRequest?.endDate)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted</label>
                  <p className="text-gray-900">{formatRelativeTime(selectedRequest?.submittedAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                  <p className="text-gray-900">{selectedRequest?.managerName}</p>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">{selectedRequest?.reason}</p>
              </div>

              {/* Actions for pending requests */}
              {selectedRequest?.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      console.log('Approve request:', selectedRequest?.id);
                      setSelectedRequest(null);
                    }}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                  >
                    Approve Request
                  </button>
                  <button
                    onClick={() => {
                      console.log('Deny request:', selectedRequest?.id);
                      setSelectedRequest(null);
                    }}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                  >
                    Deny Request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsDashboard;